import { styled } from '@mui/material/styles';
import { FC, ReactNode, useEffect, useState } from 'react';
import { Box, FormLabel } from '@mui/material';
import { ArticleSourceType, ArticleType } from 'pages/knowledge/hooks/useArticlesQuery';
import RenderPostsInEditPlayListModal from 'pages/knowledge/components/RenderPostsInEditPlaylistModal';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { useFormContext, useWatch } from 'react-hook-form';
import PostTitle from './PostTitle';
import PostDescription from './PostDescription';
import PostAttachments from './PostAttachments';
import PostTopics from './PostTopics';
import PostProject from './PostProject';
import PostPartner from './PostPartner';
import PostPlaylists from './PostPlaylists';
import PostDivisionsAndSpecSections from './PostDivisionsAndSpecSections';
import PostProjectPhases from './PostProjectPhases';
import useUserData from 'hooks/useUserData';
import { ConfigEntryType } from 'config/config';
import { useConfigQuery } from '@buildwise/libs/configurations';
import HandleOperation from 'components/utils/HandleOperation';
import { FULL_WIDTH_ARTICLE_FIELDS, PostFormSkeleton } from '@buildwise/libs/posts';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';
import { FieldWrapper } from '@buildwise/libs/people';
import QuestionAssignedToField from './QuestionAssignedToField';
import { TextFieldController } from '@buildwise/libs/ui';

type PostDetailsFormPropTypes = {
  article?: ArticleType;
  articleType: ArticleSourceType;
  configName: string;
  forward?: boolean;
  isCreatingOrEditingPlaylist?: boolean;
  isEditing: boolean;
  isFullScreen: boolean;
  loadingFiles: boolean;
  selectedTemplateId?: string;
  skeleton?: ReactNode;
  articleFields: ConfigEntryType[];
  getFieldWidth?: (options: { field: ConfigEntryType }) => string | undefined;
  onDrop: (acceptedFiles: File[]) => void;
};

const PostDetailsForm: FC<PostDetailsFormPropTypes> = ({
  article,
  articleType,
  configName,
  forward = false,
  isCreatingOrEditingPlaylist,
  isEditing,
  isFullScreen,
  loadingFiles,
  selectedTemplateId,
  skeleton,
  articleFields,
  getFieldWidth,
  onDrop,
}) => {
  const { company, companyId } = useUserData();
  const {
    control,
    formState: { isSubmitting },
    watch,
    getValues,
    setValue,
  } = useFormContext<PostFormData>();
  const title = watch('title');
  const [postTitle, setPostTitle] = useState(title);
  const { isLoading: articleFieldsLoading } = useConfigQuery({
    companyId,
    configName,
    shouldFetch: false,
  });
  const { targetGroups = [], region = [] } = useWatch({ control }) as PostFormData & {
    targetGroups: string[];
    region: string[];
  };

  const renderField = (field: ConfigEntryType, index: number) => {
    switch (field.entryCode) {
      case 'title': {
        return (
          <PostTitle
            autoFocus={index === 0}
            selectedTemplateId={selectedTemplateId}
            setPostTitle={setPostTitle}
            required={field.referenceObject?.isRequired}
          />
        );
      }

      case 'description': {
        return (
          <>
            {(articleType === ArticleSourceType.QUESTION ||
              articleType === ArticleSourceType.SUGGESTION) && (
              <Box mb={0.5}>
                <FieldLabel id={field.entryCode} disabled={isSubmitting}>
                  {field.referenceObject?.label || field.entryDescription}
                  {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
                </FieldLabel>
              </Box>
            )}
            <PostDescription
              autoFocus={index === 0}
              hideToolbar={
                articleType === ArticleSourceType.QUESTION ||
                articleType === ArticleSourceType.SUGGESTION
              }
              isEditing={isEditing}
              placeholder={`${field.referenceObject?.placeHolder || 'Add a description'}${!isCreatingOrEditingPlaylist && field.referenceObject?.isRequired && articleType === ArticleSourceType.POST ? '*' : ''}`}
            />
          </>
        );
      }

      case 'attachments': {
        return (
          <PostAttachments
            isFullScreen={isFullScreen}
            articleType={articleType}
            article={article}
            onDrop={onDrop}
            loadingFiles={loadingFiles}
          />
        );
      }

      case 'topics': {
        if (forward) {
          return null;
        }

        return <PostTopics field={field} />;
      }

      case 'projectId': {
        if (company?.settings?.projectFlag && !forward) {
          return <PostProject field={field} />;
        }

        return null;
      }

      case 'projectPhases': {
        if (forward) {
          return null;
        }

        return <PostProjectPhases field={field} />;
      }

      case 'partnerIds': {
        if (company?.settings?.partnerFlag && !forward) {
          return <PostPartner field={field} />;
        }

        return null;
      }

      case 'playListArticleIds': {
        if (isEditing || forward) {
          return null;
        }

        return <PostPlaylists field={field} />;
      }

      case 'divisions': {
        return (
          <PostDivisionsAndSpecSections
            articleFields={articleFields}
            isFullScreen={isFullScreen}
            postTitle={postTitle}
          />
        );
      }

      // Till the time we separate specSections field as an individual component from PostDivisionsAndSpecSections, return null
      case 'specSections': {
        return null;
      }

      case 'taskOwner': {
        return (
          <QuestionAssignedToField
            autoFocus={index === 0}
            departments={targetGroups}
            regions={region}
          />
        );
      }

      case 'comment': {
        return (
          <>
            <Box mb={1}>
              <FieldLabel id={field.entryCode} disabled={isSubmitting}>
                {field.referenceObject?.label || field.entryDescription}
                {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
              </FieldLabel>
            </Box>
            <TextFieldController<PostFormData>
              name="comment"
              TextFieldProps={{
                id: 'comment',
                variant: 'outlined',
                multiline: true,
                minRows: 5,
                autoFocus: index === 0,
                placeholder: field.referenceObject?.placeHolder,
              }}
            />
          </>
        );
      }

      default: {
        return <RenderFilterFieldByType autoFocus={index === 0} field={field} />;
      }
    }
  };

  // When we reset the title field through resetField method
  // the value of postTitle is not getting updated
  useEffect(() => {
    if (!title) {
      setPostTitle(title);
    }
  }, [title]);

  return (
    <>
      <HandleOperation
        isLoading={articleFieldsLoading}
        skelton={skeleton || <PostFormSkeleton />}
        skeltonCount={1}
      >
        {articleFields?.map((field, index) => {
          const _field = renderField(field, index);

          if (_field) {
            return (
              <Box
                key={field.entryCode}
                width={
                  getFieldWidth?.({ field }) ||
                  (FULL_WIDTH_ARTICLE_FIELDS.includes(field.entryCode)
                    ? '100%'
                    : isFullScreen
                      ? '50%'
                      : '70%')
                }
                mt={FULL_WIDTH_ARTICLE_FIELDS.includes(field.entryCode) ? 0 : 1}
              >
                {FULL_WIDTH_ARTICLE_FIELDS.includes(field.entryCode) ? (
                  _field
                ) : (
                  <FieldWrapper
                    id={field.entryCode}
                    label={field.referenceObject?.label || field.entryDescription}
                    required={!isCreatingOrEditingPlaylist && field.referenceObject?.isRequired}
                    disabled={isSubmitting}
                  >
                    {_field}
                  </FieldWrapper>
                )}
              </Box>
            );
          }
          return null;
        })}
        {article?.originIndicator && article?.originIndicator === 'PLAYLIST' && (
          <RenderPostsInEditPlayListModal
            playListArticles={article?.playListArticles}
            article={article}
            getValues={getValues}
            setValue={setValue}
          />
        )}
      </HandleOperation>
    </>
  );
};

export const FieldLabel = styled(FormLabel)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
  marginTop: 40,
  whiteSpace: 'pre-wrap',
}));

export const Asterisk = styled('span')(({ theme }) => ({
  color: theme.palette.custom.paleCarmine,
}));

export default PostDetailsForm;
